import React, { useMemo } from "react";
import Moment from "react-moment";
import { createColumnHelper } from "@tanstack/react-table";
import Amount from "../../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import MetricSummaryTile from "../../../ui-components/metric-summary-tile/metricSummaryTile";
import Table from "../../../ui-components/table/table";
import Tag from "../../../ui-components/molecules/tag/tag";
import {
  PaymentHealth,
  PaymentHealthRecentFailure,
} from "../../../app/data/payment/PaymentSession";
import PaymentSummarySection from "./paymentSummarySection";
import { formatDuration, formatPct, HEALTH_WINDOW_OPTIONS, statusColor } from "./paymentAdminUtils";

const failureColumnHelper = createColumnHelper<PaymentHealthRecentFailure>();

export interface PaymentHealthSectionProps {
  healthHours: number;
  health: PaymentHealth | null;
  healthError: string | null;
  isLoading: boolean;
  onHoursChange: (hours: number) => void;
  onRefresh: () => void;
  onFailureClick: (paymentId: string) => void;
}

const PaymentHealthSection: React.FC<PaymentHealthSectionProps> = ({
  healthHours,
  health,
  healthError,
  isLoading,
  onHoursChange,
  onRefresh,
  onFailureClick,
}) => {
  const windowOption =
    HEALTH_WINDOW_OPTIONS.find((o) => o.value === String(healthHours)) ?? HEALTH_WINDOW_OPTIONS[1];

  const failureColumns = useMemo(
    () => [
      failureColumnHelper.accessor("status", {
        header: "Status",
        cell: (info) => (
          <Tag mods={{ color: statusColor(info.getValue()) }}>{info.getValue()}</Tag>
        ),
      }),
      failureColumnHelper.accessor("provider", { header: "Provider" }),
      failureColumnHelper.accessor("accountNumber", {
        header: "Account",
        cell: (info) => info.getValue() ?? "—",
      }),
      failureColumnHelper.accessor("amountCents", {
        header: "Amount",
        cell: (info) =>
          info.getValue() != null ? <Amount amount={(info.getValue() as number) / 100} /> : "—",
      }),
      failureColumnHelper.accessor("dateCreated", {
        header: "Date",
        cell: (info) =>
          info.getValue() ? (
            <Moment format="MM/DD/YYYY h:mm A">{info.getValue()}</Moment>
          ) : (
            "—"
          ),
      }),
      failureColumnHelper.accessor("paymentId", {
        header: "Payment ID",
        cell: (info) => <span className="xgs-payment-admin__id">{info.getValue()}</span>,
      }),
    ],
    []
  );

  return (
    <div className="xgs-payment-admin__health">
      <div className="xgs-payment-admin__filters">
        <LabeledSelectInput
          label="Window"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          options={HEALTH_WINDOW_OPTIONS}
          value={windowOption}
          onValueChange={(option) => onHoursChange(Number(option?.value ?? 24))}
        />
        <Button theme={ButtonThemes.blue} onClick={onRefresh}>
          Refresh
        </Button>
      </div>
      {healthError && <XGSErrorMessage>{healthError}</XGSErrorMessage>}
      {health && (
        <>
          <div className="xgs-payment-admin__card">
            <div className="xgs-payment-admin__summary">
              <PaymentSummarySection title="Queue">
                <MetricSummaryTile
                  iconPreset="filePurplePending"
                  colorVariant="purple"
                  isLoading={isLoading}
                  title="Pending now"
                  value={health.pendingCount}
                  legendDescription="Payments currently in a pending state."
                />
                <MetricSummaryTile
                  iconPreset="exclamationRed"
                  colorVariant="red"
                  isLoading={isLoading}
                  title="Stuck pending"
                  value={health.stuckPendingCount}
                  legendDescription="Pending payments exceeding the stuck threshold."
                />
                <MetricSummaryTile
                  iconPreset="clockYellow"
                  colorVariant="yellow"
                  isLoading={isLoading}
                  title="Oldest pending"
                  value={formatDuration(health.oldestPendingAgeSeconds)}
                  legendDescription="Age of the oldest currently pending payment."
                />
              </PaymentSummarySection>

              <PaymentSummarySection title={`Window performance (${health.windowHours}h)`}>
                <MetricSummaryTile
                  iconPreset="checkBlueCircle"
                  colorVariant="green"
                  isLoading={isLoading}
                  title="Success rate"
                  value={formatPct(health.successRate)}
                  legendDescription="Completed payments in the selected window."
                />
                <MetricSummaryTile
                  iconPreset="exclamationRed"
                  colorVariant="red"
                  isLoading={isLoading}
                  title="Fail rate"
                  value={formatPct(health.failRate)}
                  legendDescription="Failed payments in the selected window."
                />
                <MetricSummaryTile
                  iconPreset="fileGreenCheck"
                  colorVariant="green"
                  isLoading={isLoading}
                  title="Completed"
                  value={health.completedCount}
                  legendDescription="Successfully completed payments in the window."
                />
                <MetricSummaryTile
                  iconPreset="exclamationRed"
                  colorVariant="red"
                  isLoading={isLoading}
                  title="Failed"
                  value={health.failedCount}
                  legendDescription="Failed payments in the window."
                />
                <MetricSummaryTile
                  iconPreset="clockYellow"
                  colorVariant="yellow"
                  isLoading={isLoading}
                  title="Expired"
                  value={health.expiredCount}
                  legendDescription="Expired payments in the window."
                />
              </PaymentSummarySection>

              <PaymentSummarySection title="Sync">
                <MetricSummaryTile
                  iconPreset="clockYellow"
                  colorVariant="yellow"
                  isLoading={isLoading}
                  title="Sync lag"
                  value={formatDuration(health.syncLagSeconds)}
                  legendDescription="Time since the last successful sync run."
                />
                <MetricSummaryTile
                  iconPreset="fileBlue"
                  colorVariant="blue"
                  isLoading={isLoading}
                  title="Last sync candidates"
                  value={health.lastSyncCandidateCount}
                  legendDescription="Payments evaluated in the most recent sync run."
                />
              </PaymentSummarySection>
            </div>
          </div>

          <div className="xgs-payment-admin__card">
            <h4 className="xgs-payment-admin__card__title">Recent failed / expired</h4>
            {(health.recentFailures || []).length === 0 ? (
              <p className="xgs-payment-admin__empty">No recent exceptions.</p>
            ) : (
              <Table
                columns={failureColumns}
                data={health.recentFailures || []}
                onRowClicked={(row: PaymentHealthRecentFailure) => onFailureClick(row.paymentId)}
                cursorPointer
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentHealthSection;
